import { Component, inject, OnInit, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DTypeForm, DTypeFormTriggerField } from '@grupo-constant/next-dtype/form';
import { NextLocator, TriggerEvent, TriggerShortcutExpr, TRIGGER_COLUMN_URI } from '@grupo-constant/next-dtype/types';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatISO, startOfToday } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { AbsenceIntervalCalendarComponent } from '../review-absence-dialog/absence-interval-calendar.component';
import { DialogsService } from '../dialogs.service';
import { DTypeModule } from '../../core/dtype/dtype.module';
import { CompanyService } from '../../core/company/company.service';
import { DTypeService } from '../../core/dtype/dtype.service';
import { FormService } from '../../core/form/form.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { LocalizePipe } from '../../core/pipes/localize.pipe';
import { ServicesService } from '../../modules/admin/services/services.service';
import { UserService } from '../../core/user/user.service';
import { FuseConfirmationDialogComponent } from '../../../@fuse/services/confirmation/dialog/dialog.component';

@Component({
    selector: 'app-new-review-absence-dialog',
    templateUrl: './new-review-absence-dialog.component.html',
    imports: [AbsenceIntervalCalendarComponent, DTypeModule, MatButtonModule, MatIconModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [
        /* language=SCSS */
        `
            .new-review-absence-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ]
})
export class NewReviewAbsenceDialogComponent implements OnInit {

    dTypeForm: DTypeForm;
    intervalFrom = '';
    intervalTo = '';
    intervalWeekDays: string[] = [];

    private companyService = inject(CompanyService);
    private data: { locator: NextLocator, formUri: string } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<NewReviewAbsenceDialogComponent>);
    private dialogsService = inject(DialogsService);
    private dTypeService = inject(DTypeService);
    private formService = inject(FormService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private localize = inject(LocalizePipe);
    private servicesService = inject(ServicesService);
    private transloco = inject(TranslocoService);
    private userService = inject(UserService);
    private destroyRef = inject(DestroyRef);

    async ngOnInit() {
        this.dTypeForm = await this.dTypeService.getDtypeFormByUri({
            uri: this.data.formUri,
            locator: this.data.locator,
            bindableOptions: true
        }, {});
        await this.fillTriggerFields([TriggerEvent.ON_OPEN_RESET]);

        const reviewCommentField = this.dTypeForm.fields['reviewComment'];
        if (reviewCommentField) {
            reviewCommentField.hidden = false;
            reviewCommentField.readonly = false;
            reviewCommentField.control.enable();
        }

        const userIdField = this.dTypeForm.fields['userId'];
        if (userIdField) {
            userIdField.hidden = false;
            userIdField.readonly = false;
            userIdField.control.enable();
        }
        this.refreshInterval();
        for (const name of ['from', 'to', 'weekDays']) {
            this.dTypeForm.control.get(name)?.valueChanges
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.refreshInterval());
        }
    }

    create() {
        this.dTypeForm.control.markAllAsTouched();
        if (this.dTypeForm.control.invalid) {
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('errorsInForm')
            });
            return;
        }
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.absences.createConfirm'));
        confirmDialog.afterClosed().subscribe(async (confirm) => {
            if (confirm === 'confirmed') {
                try {
                    await this.fillTriggerFields([
                        TriggerEvent.ON_CREATE,
                        TriggerEvent.ON_QUEUE,
                        TriggerEvent.ON_QUEUE_RESET,
                        TriggerEvent.ON_SENDING]);
                    await firstValueFrom(this.formService.flushFormData({
                        data: this.dTypeForm.control.getRawValue(),
                        serviceId: this.data.locator.serviceId,
                        uri: this.data.formUri
                    }));
                    this.closeDialog(true);
                } catch (error) {
                    console.error(error);
                    this.dialogsService.openErrorDialog({
                        message: error?.echo && Object.keys(error.echo).length ? this.localize.transform(error.echo) : undefined
                    });
                }
            }
        });
    }

    closeDialog(sended?: boolean): void {
        this.dialogRef.close(sended);
    }

    private refreshInterval() {
        this.intervalFrom = this.isoDate(this.dTypeForm?.control.get('from')?.value);
        const to = this.isoDate(this.dTypeForm?.control.get('to')?.value);
        this.intervalTo = to || this.intervalFrom;
        const weekDays = this.dTypeForm?.control.get('weekDays')?.value;
        this.intervalWeekDays = Array.isArray(weekDays) ? [...weekDays] : [];
    }

    private isoDate(value: unknown): string {
        return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.substring(0, 10) : '';
    }

    private async fillTriggerFields(event: TriggerEvent[]) {
        for (const field of Object.values(this.dTypeForm.fields)
            .filter(f => f.type === TRIGGER_COLUMN_URI)) {
            const needsToBeFilled = this.formService.checkTriggerField({
                value: this.dTypeForm.control.get(field.name).value,
                triggerField: (field as DTypeFormTriggerField),
                event
            });

            if (needsToBeFilled) {
                this.dTypeForm.control.get(field.name)
                    .setValue(await this.getTriggerFieldValue((field as DTypeFormTriggerField)));
            }
        }
    }

    private async getTriggerFieldValue(triggerField: DTypeFormTriggerField) {
        let value: any = null;
        switch (triggerField.shortcutExpr) {
            case TriggerShortcutExpr.userId:
                const user = await firstValueFrom(this.userService.get());
                value = user?.userId;
                break;
            case TriggerShortcutExpr.companyId:
                const company = await firstValueFrom(this.companyService.get());
                value = company.companyId;
                break;
            case TriggerShortcutExpr.ISODatetime:
            case TriggerShortcutExpr.ISOTime:
                value = formatISO(new Date());
                break;
            case TriggerShortcutExpr.ISODate:
                value = formatISO(startOfToday());
                break;
            case TriggerShortcutExpr.serviceId:
                value = this.data.locator.serviceId;
                break;
            case TriggerShortcutExpr.formUri:
                value = this.dTypeForm.dType.uri;
                break;
            default:
                break;
        }
        return value;
    }

    private openConfirmDialog(message: string): MatDialogRef<FuseConfirmationDialogComponent, any> {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

}
